import { Autocomplete, FormGroup, FormHelperText, FormLabel, MenuItem, Select, TextField } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { createCollaboratorNotification, CollaboratorNotificationTarget, CreateCollaboratorNotificationPayload } from "src/apis/collaboratorNotifications";
import { Collaborator, filterVisibleCompanies, GetCompanies, getCollaboratorsByCompany } from "src/apis/company";
import { createNotification, CreateNotificationPayload, getUsers as getAllUsers, NotificationTarget, User } from "src/apis/user";
import FormSaveAndResetButtons from "src/pages/components/FormSaveAndResetButtons";
import PageContainer from "src/pages/components/PageContainer";
import CompanySelect from "src/pages/components/CompanySelect";
import CollaboratorSelect from "src/pages/components/CollaboratorSelect";
import { isSuccessOrAllRequiredFieldsAreFilled } from "src/utils/form";
import { startNotificationQueueProcessing } from "src/utils/notificationQueue";

const defaultValues: CreateNotificationPayload = {
  target: NotificationTarget.ALL_USERS,
  published_to_user_id: 0,
  subject: "",
  message: "",
}

const defaultCollaboratorValues: CreateCollaboratorNotificationPayload = {
  target: CollaboratorNotificationTarget.ALL_COLLABORATORS,
  company_id: "",
  collaborator_id: "",
  subject: "",
  message: "",
}

type NotificationAudience = "USERS" | "COLLABORATORS";

const RegisterNotificationPage: FC = () => {
  const [error, setError] = useState<string>("");
  const [formValues, setFormValues] = useState<CreateNotificationPayload>(defaultValues);
  const [collaboratorValues, setCollaboratorValues] = useState<CreateCollaboratorNotificationPayload>(defaultCollaboratorValues);
  const [collaborator, setCollaborator] = useState<Collaborator | null>(null);
  const [audience, setAudience] = useState<NotificationAudience>("USERS");
  const [users, setUsers] = useState<User[]>([]);
  const [success, setSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const autocompleUsers = useMemo(
    () => users?.map(u => ({label: `${u?.name} (${u?.company})`, value: u.id,})),
    [users])

  const collaboratorOptionalFields = useMemo(() => {
    if (collaboratorValues.target === CollaboratorNotificationTarget.ALL_COLLABORATORS) {
      return ["company_id", "collaborator_id"];
    }

    if (collaboratorValues.target === CollaboratorNotificationTarget.COMPANY_COLLABORATORS) {
      return ["collaborator_id"];
    }

    return [];
  }, [collaboratorValues.target]);

  const isSubmitDisabled =  useMemo(() => {
    if (audience === "COLLABORATORS") {
      return isSuccessOrAllRequiredFieldsAreFilled(success, collaboratorValues, collaboratorOptionalFields);
    }

    return isSuccessOrAllRequiredFieldsAreFilled(success, formValues, ["published_to_user_id"]);
  }, [audience, collaboratorOptionalFields, collaboratorValues, formValues, success]);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true)

    try {
      if (audience === "COLLABORATORS") {
        const basePayload = {
          ...collaboratorValues,
          target: CollaboratorNotificationTarget.ONE_COLLABORATOR,
        };

        if (collaboratorValues.target === CollaboratorNotificationTarget.ONE_COLLABORATOR) {
          await createCollaboratorNotification({
            ...basePayload,
            company_id: collaboratorValues.company_id ? Number(collaboratorValues.company_id) : undefined,
            collaborator_id: collaboratorValues.collaborator_id ? Number(collaboratorValues.collaborator_id) : null,
          });
        } else if (collaboratorValues.target === CollaboratorNotificationTarget.COMPANY_COLLABORATORS) {
          const companyId = Number(collaboratorValues.company_id);

          if (!companyId) {
            throw new Error("Selecione uma empresa para enviar o comunicado.");
          }

          const collaborators = await getCollaboratorsByCompany(companyId);

          for (const collaboratorItem of collaborators) {
            await createCollaboratorNotification({
              ...basePayload,
              company_id: companyId,
              collaborator_id: collaboratorItem.id_colaborador,
            });
          }
        } else {
          const companiesResponse = await GetCompanies();
          const visibleCompanies = filterVisibleCompanies(companiesResponse.data ?? []);

          for (const company of visibleCompanies) {
            const collaborators = await getCollaboratorsByCompany(company.id);

            for (const collaboratorItem of collaborators) {
              await createCollaboratorNotification({
                ...basePayload,
                company_id: company.id,
                collaborator_id: collaboratorItem.id_colaborador,
              });
            }
          }
        }
      } else {
        await createNotification(formValues)
      }

      setError("")
      setSuccess(true)
    } catch (err: any) {
      setError(err?.message)
      setSuccess(false)
    } finally {
      setLoading(false)
    }
  }

  async function fetchUsers() {
    try {
      const res = await getAllUsers();

      setUsers(res)
    } catch (err: any) {
      setError(err?.message)
      setSuccess(false)
    }
  }

  useEffect(() => {
    if (audience === "USERS") {
      fetchUsers()
    }
  }, [audience])

  useEffect(() => {
    startNotificationQueueProcessing();
  }, []);

  return (
    <PageContainer title="Novo comunicado" error={error}>
        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
        >
          <FormGroup>
            <FormLabel>Destino do comunicado</FormLabel>
            <Select
              variant="outlined"
              value={audience}
              onChange={(e) => {
                const value = e.target.value as NotificationAudience;

                setAudience(value);
                setSuccess(false);
                setError("");
                setFormValues(defaultValues);
                setCollaboratorValues(defaultCollaboratorValues);
                setCollaborator(null);
              }}
            >
              <MenuItem value="USERS">Usuários do sistema</MenuItem>
              <MenuItem value="COLLABORATORS">Colaboradores (portal externo)</MenuItem>
            </Select>
            <FormHelperText>
              Selecione o público que deve receber esse comunicado
            </FormHelperText>
          </FormGroup>

          {audience === "USERS" ? (
            <>
              <FormGroup>
                <FormLabel>
                  Quem notificar
                </FormLabel>
                <Select
                  variant="outlined"
                  value={formValues?.target}
                  onChange={(e) => {
                    setFormValues((old) => ({
                      ...old,
                      target: e.target.value as NotificationTarget
                    }))
                  }}
                >
                  <MenuItem value={NotificationTarget.ALL_USERS}>
                    Todos os usuários do sistema
                  </MenuItem>
                  <MenuItem value={NotificationTarget.ALL_COMPANIES}>
                    Todas as empresas
                  </MenuItem>
                  <MenuItem value={NotificationTarget.ALL_PSYCHOLOGISTS}>
                    Todos os psicólogos
                  </MenuItem>
                  <MenuItem value={NotificationTarget.ONE_USER}>
                    Usuário específico
                  </MenuItem>
                </Select>
                <FormHelperText>
                  O grupo de usuários que deve receber esse comunicado
                </FormHelperText>
              </FormGroup>

              {formValues?.target === NotificationTarget.ONE_USER && (
                <FormGroup>
                  <FormLabel>
                    Usuário a ser notificado
                  </FormLabel>
                  <Autocomplete
                    options={autocompleUsers}
                    getOptionLabel={(option) => option.label}
                    getOptionKey={(option) => option?.value}
                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                    renderInput={(params) => <TextField {...params} />}
                    onChange={(_, selected) => {
                      setFormValues((old) => ({
                        ...old,
                        published_to_user_id: selected?.value
                      }))
                    }}
                  />
                  <FormHelperText>
                    O usuário que vai receber as notificações
                  </FormHelperText>
                </FormGroup>
              )}
            </>
          ) : (
            <>
              <FormGroup>
                <FormLabel>Quem notificar</FormLabel>
                <Select
                  variant="outlined"
                  value={collaboratorValues?.target}
                  onChange={(e) => {
                    const target = e.target.value as CollaboratorNotificationTarget;

                    setCollaboratorValues((old) => ({
                      ...old,
                      target,
                      company_id: target === CollaboratorNotificationTarget.ALL_COLLABORATORS ? "" : old.company_id,
                      collaborator_id: target === CollaboratorNotificationTarget.ONE_COLLABORATOR ? old.collaborator_id : "",
                    }))
                    if (target !== CollaboratorNotificationTarget.ONE_COLLABORATOR) {
                      setCollaborator(null);
                    }
                  }}
                >
                  <MenuItem value={CollaboratorNotificationTarget.ALL_COLLABORATORS}>
                    Todos os colaboradores
                  </MenuItem>
                  <MenuItem value={CollaboratorNotificationTarget.COMPANY_COLLABORATORS}>
                    Todos os colaboradores de uma empresa
                  </MenuItem>
                  <MenuItem value={CollaboratorNotificationTarget.ONE_COLLABORATOR}>
                    Colaborador específico
                  </MenuItem>
                </Select>
                <FormHelperText>
                  O grupo de colaboradores que deve receber o comunicado no portal
                </FormHelperText>
              </FormGroup>

              {collaboratorValues.target !== CollaboratorNotificationTarget.ALL_COLLABORATORS && (
                <CompanySelect
                  companyId={collaboratorValues.company_id ?? ""}
                  setCompanyId={(companyId) => {
                    setCollaboratorValues((old) => ({
                      ...old,
                      company_id: companyId,
                      collaborator_id: "",
                    }));
                    setCollaborator(null);
                  }}
                  setError={setError}
                  helperText="Selecione a empresa dos colaboradores."
                />
              )}

              {collaboratorValues.target === CollaboratorNotificationTarget.ONE_COLLABORATOR && (
                <CollaboratorSelect
                  companyId={collaboratorValues.company_id ?? ""}
                  collaboratorId={collaboratorValues.collaborator_id ?? ""}
                  setCollaborator={(selected) => {
                    setCollaborator(selected);
                    setCollaboratorValues((old) => ({
                      ...old,
                      collaborator_id: selected.id_colaborador,
                    }));
                  }}
                  unsetCollaborator={() => {
                    setCollaborator(null);
                    setCollaboratorValues((old) => ({
                      ...old,
                      collaborator_id: "",
                    }));
                  }}
                  setError={setError}
                  initialValue={collaborator}
                  helperText="Pesquise o colaborador pelo nome."
                  disabled={!collaboratorValues.company_id}
                />
              )}
            </>
          )}

          <FormGroup>
            <FormLabel>Assunto</FormLabel>
            <TextField
              name="subject"
              type="text"
              value={audience === "COLLABORATORS" ? collaboratorValues?.subject : formValues?.subject}
              onChange={(e) => {
                if (audience === "COLLABORATORS") {
                  setCollaboratorValues(old => ({
                    ...old,
                    subject: e.target.value as string,
                  }));
                } else {
                  setFormValues(old => ({
                    ...old,
                    subject: e.target.value as string,
                  }));
                }
              }}
            />
            <FormHelperText>
              O assunto ou título do comunicado
            </FormHelperText>
          </FormGroup>

          <FormGroup>
            <FormLabel>Mensagem</FormLabel>
            <TextField
              multiline
              minRows={4}
              name="message"
              type="text"
              value={audience === "COLLABORATORS" ? collaboratorValues?.message : formValues?.message}
              onChange={(e) => {
                if (audience === "COLLABORATORS") {
                  setCollaboratorValues(old => ({
                    ...old,
                    message: e.target.value as string,
                  }));
                } else {
                  setFormValues(old => ({
                    ...old,
                    message: e.target.value as string,
                  }));
                }
              }}
            />
            <FormHelperText>
              Mensagem ou descrição do comunicado ({audience === "COLLABORATORS" ? collaboratorValues.message.length : formValues.message.length} de 1000 caracteres)
            </FormHelperText>
          </FormGroup>

          <FormSaveAndResetButtons
            success={success}
            loading={loading}
            isSubmitDisabled={isSubmitDisabled}
            resetFormState={() => {}}
            isResetFormStateDisabled={true}
          />
      </form>
    </PageContainer>
  );
}

export default RegisterNotificationPage;
